import React, { useContext, useState, useEffect } from 'react';
import { setCookie } from './browser-cookies';
import { AppContext, REG_TYPES } from '../Store';
import { NicknameFieldContext } from '../state-management/contexts/regform-fields/nickname-field-context';
import { PasswordFieldContext } from '../state-management/contexts/regform-fields/password-field-context';
import { EmailFieldContext } from '../state-management/contexts/regform-fields/email-field-context';
import { TermsCheckboxContext } from '../state-management/contexts/regform-checkboxes/terms-checkbox-context';
import { registerWithAllFields, registerWithoutEmail, registerWithoutNickname } from './form-registrations';
import { OneLPSsdkParamsContext } from '../onelps-sdk-params-context';

export const COOKIE_NOTIFICATION_NAME = 'CookieNotification';
export const COOKIE_NOTIFICATION_VALUE_ACCEPT = '1';
export const COOKIE_NOTIFICATION_VALUE_REJECT = '0';
export const COOKIE_NOTIFICATION_EXPIRATION_DATE = 365;
export const THIRD_PARTY_ACCEPTANCE_URL_PARAM = 'accepted_3rd_party_pixels';

export const withCookieOverlayAfterForm = function(Component) {
	const EnhancedComponent = function({ ...props }) {
		const [state, dispatch] = useContext(AppContext);
		const [nicknameState, nicknameDispatch] = useContext(NicknameFieldContext);
		const [passwordState, passwordDispatch] = useContext(PasswordFieldContext);
		const [emailState, emailDispatch] = useContext(EmailFieldContext);
		const [termsState, termsDispatch] = useContext(TermsCheckboxContext);
		const onelpsParams = useContext(OneLPSsdkParamsContext);

		const [shouldRegister, setShouldRegister] = useState(false);
		const [onRegFailureCallback, setOnRegFailureCallback] = useState(null);
		const [shouldRedirectToIpp, setShouldRedirectToIpp] = useState(false);
		const [finalIppUrl, setFinalIppUrl] = useState('');
		const [isThirdPartyTrackingAccepted, setIsThirdPartyTrackingAccepted] = useState(false);

		async function makeRegistrationWithoutNickname() {
			await registerWithoutNickname(state.locale, dispatch, emailDispatch, passwordDispatch, termsDispatch,
				emailState.value, passwordState.value, termsState.value, isThirdPartyTrackingAccepted, onRegFailureCallback);
		}

		async function makeRegistrationWithoutEmail() {
			await registerWithoutEmail(state.locale, dispatch, nicknameDispatch, passwordDispatch, termsDispatch,
				nicknameState.value, passwordState.value, termsState.value, isThirdPartyTrackingAccepted, state.race, onRegFailureCallback);
		}

		async function makeRegistrationWithAllFields() {
			await registerWithAllFields(state.locale, dispatch, nicknameDispatch, passwordDispatch, emailDispatch, termsDispatch,
				nicknameState.value, passwordState.value,
				emailState.value, termsState.value, isThirdPartyTrackingAccepted, state.race, onRegFailureCallback
			);
		}

		useEffect(() => {
			async function callRegisterFunction() {
				if (state.trackingExecuted && shouldRegister) {
					let regFn;
	
					switch (onelpsParams.form.type) {
					case 'regWithAllFields':
						regFn = makeRegistrationWithAllFields;
						break;
					case 'regWithoutNickname':
						regFn = makeRegistrationWithoutNickname;
						break;
					case 'regWithoutEmail':
						regFn = makeRegistrationWithoutEmail;
						break;
					default:
						regFn = makeRegistrationWithAllFields;
						break;
					}
					regFn();
				}
			}
			callRegisterFunction();
		}, [state.trackingExecuted, shouldRegister]);

		useEffect(() => {
			if (state.trackingExecuted && shouldRedirectToIpp) {
				window.location.href = finalIppUrl;
			}
		}, [state.trackingExecuted, shouldRedirectToIpp, finalIppUrl]);

		return (
			<Component
				{...props}
				shouldShowCookieOverlay={state.showCookieOverlay}
				onGoBackButtonClick={() => dispatch({ type: 'HIDE_COOKIE_OVERLAY' })}
				onOverlayCTAButtonClick={async (e, ippUrl, onFailureCallbackFn) => {
					e ? e.preventDefault() : null;
					setOnRegFailureCallback(() => onFailureCallbackFn);
					setCookie(COOKIE_NOTIFICATION_NAME, COOKIE_NOTIFICATION_VALUE_ACCEPT, COOKIE_NOTIFICATION_EXPIRATION_DATE);
					dispatch({ type: 'ACCEPT_THIRD_PARTY_TRACKING' });
					setIsThirdPartyTrackingAccepted(true);

					if (state.regType === REG_TYPES.STANDARD) {
						setShouldRegister(true);
					} else if (state.regType === REG_TYPES.IPP) {
						setFinalIppUrl(ippUrl + COOKIE_NOTIFICATION_VALUE_ACCEPT);
						setShouldRedirectToIpp(true);
					}
				}}
				onRejectButtonClick={async (e, ippUrl) => {
					e ? e.preventDefault() : null;
					setCookie(COOKIE_NOTIFICATION_NAME, COOKIE_NOTIFICATION_VALUE_REJECT, COOKIE_NOTIFICATION_EXPIRATION_DATE);

					if (state.regType === REG_TYPES.STANDARD) {
						dispatch({ type: 'MARK_TRACKING_EXECUTED' });
						setShouldRegister(true);
					} else if (state.regType === REG_TYPES.IPP) {
						dispatch({ type: 'MARK_TRACKING_EXECUTED' });
						setFinalIppUrl(ippUrl + COOKIE_NOTIFICATION_VALUE_REJECT);
						setShouldRedirectToIpp(true);
					}
				}}
			/>
		);
	};
	EnhancedComponent.displayName = 'componentWithCookieOverlay';
	return EnhancedComponent;
};

export const withCookieOverlayWithoutRegAttempt = function(Component) {
	const EnhancedComponent = function({ ...props }) {
		const [state, dispatch] = useContext(AppContext);

		return (
			<Component
				{...props}
				shouldShowCookieOverlay={state.showCookieOverlay}
				onGoBackButtonClick={() => {}}
				onOverlayCTAButtonClick={(e) => {
					e && e.preventDefault();
					setCookie(COOKIE_NOTIFICATION_NAME, COOKIE_NOTIFICATION_VALUE_ACCEPT, COOKIE_NOTIFICATION_EXPIRATION_DATE);
					dispatch({ type: 'HIDE_COOKIE_OVERLAY' });
					dispatch({ type: 'ACCEPT_THIRD_PARTY_TRACKING' });
				}}
				onRejectButtonClick={(e) => {
					e.preventDefault();
					setCookie(COOKIE_NOTIFICATION_NAME, COOKIE_NOTIFICATION_VALUE_REJECT, COOKIE_NOTIFICATION_EXPIRATION_DATE);
					dispatch({ type: 'HIDE_COOKIE_OVERLAY' });
				}}
			/>
		);
	};
	EnhancedComponent.displayName = 'componentWithCookieOverlay';
	return EnhancedComponent;
};
